<script setup>
import HpoPatientModalSection from "@/components/hpo/patientModal/HpoPatientModalSection.vue";
import {computed, ref, useTemplateRef, watch} from "vue";
import {pgettext} from "@/utils/translate";
import {reverseUrl} from "@/url";
import {createFormData} from "@/csrf";
import PatientImage from "@/components/hpo/dashboard/patientListItems/PatientImage.vue";
import FormErrorDialog from "@/components/hpo/FormErrorDialog.vue";
import CameraCapture from "@/components/camera/CameraCapture.vue";
import {mdiCamera} from "@mdi/js";
import {useDisplay} from "vuetify";

const props = defineProps({
  patient: {
    type: Object,
    required: true,
  }
});
const emit = defineEmits(['close']);
const imageInput = useTemplateRef('imageInput');
const errorDialog = useTemplateRef('errorDialog');

const patientId = computed(() => {
  return props.patient.id;
});

const isUploading = ref(false);
const showCameraCapture = ref(false);

const imageList = ref(Array.from(props.patient.images));
watch(() => props.patient.images, () => {
  imageList.value = Array.from(props.patient.images);
});

let imageQueue = ref([]);
let imagesTotal = ref(0);
let imagesComplete = ref(0);

function submitFormImage() {
  if (imageInput.value.files.length === 0) {
    return;
  }

  for (const file of imageInput.value.files) {
    addFileToQueue(file);
  }
}

function captureImage({file}) {
  if (file) {
    addFileToQueue(file);
  }
}

function addFileToQueue(file) {
  imageQueue.value.push(file);
  imagesTotal.value += 1;

  if (imagesTotal.value === 1) {
    doImageUpload();
  }
}

async function doImageUpload() {
  isUploading.value = true;
  while (imageQueue.value.length > 0) {
    const file = imageQueue.value.shift();
    await uploadImage(file);
    console.log('Upload complete');
    imagesComplete.value += 1;
  }

  // Reset the upload state
  imageInput.value.value = '';
  isUploading.value = false;
  imagesComplete.value = 0;
  imagesTotal.value = 0;
}

async function uploadImage(imageFile) {
  const formData = createFormData();
  formData.set('patient', patientId.value);
  formData.set('image_file', imageFile);

  try {
    const response = await fetch(reverseUrl('web:hpo:upload_person_image'), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const responseData = await response.json();
      errorDialog.value.showValidationError(responseData, true);
      return;
    }

    const responseData = await response.json();
    imageList.value.push(responseData.image);
  } catch (e) {
    console.log(e);
    errorDialog.value.showMutationError(e);
  }
}

const {mdAndUp} = useDisplay();
</script>

<template>
  <HpoPatientModalSection :title="pgettext('label', 'Photos')">
    <template v-slot:body>
      <v-card class="mt-4" border>
        <v-card-title>{{ pgettext('title', 'Add Photo') }}</v-card-title>

        <v-card-text>
          <div class="d-flex flex-column ga-4 flex-md-row">
            <div>
              <v-btn @click="showCameraCapture = true" :prepend-icon="mdiCamera" stacked>
                {{ pgettext('action', 'Open camera') }}
              </v-btn>
            </div>
            <v-divider :vertical="mdAndUp"/>
            <div class="flex-grow-1">
              <h4 class="mb-2">{{ pgettext('title', 'Upload a file') }}</h4>

              <div style="min-height: 32px" class="position-relative">
                <v-progress-linear v-if="isUploading"
                                   class="position-absolute"
                                   v-model="imagesComplete"
                                   :max="imagesTotal"
                                   color="green"
                                   :height="16"
                                   striped
                                   rounded>
                  <strong>{{ imagesComplete }} / {{ imagesTotal }}</strong>
                </v-progress-linear>
                <input v-show="!isUploading" type="file" ref="imageInput" accept="image/*" multiple/>
              </div>

              <div class="mt-2">
                <v-btn @click="submitFormImage" :disabled="isUploading">
                  {{ pgettext('action', 'Upload') }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <section v-if="imageList.length > 0" class="mt-6 d-flex ga-4 flex-wrap">
        <PatientImage v-for="image in imageList" :key="image.id" :image="image"/>
      </section>
      <v-empty-state v-else :title="pgettext('message', 'No photos uploaded yet.')"/>
    </template>
  </HpoPatientModalSection>

  <CameraCapture v-if="showCameraCapture" @image="captureImage" @close="showCameraCapture = false"/>
  <FormErrorDialog ref="errorDialog"/>
</template>
